// feat: service to handle podcast API operations respecting API contract
import { auth, getCookie } from './auth.js';
import { ApiError } from './api.js';
import {
  PodcastResponse,
  PodcastCreateInput,
  PodcastItem,
} from '../../../shared/src/types/podcast.js';

const API_BASE_URL = import.meta.env.VITE_API_URL;

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: {
    code: string;
    message: string;
  };
}

function getAuthHeaders() {
  const token = getCookie('vault_access_token');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

export const podcastService = {
  async list(): Promise<PodcastItem[]> {
    const response = await fetch(`${API_BASE_URL}/podcasts`, {
      headers: { Authorization: `Bearer ${getCookie('vault_access_token')}` },
    });
    if (!response.ok) throw new Error('Error fetching podcasts');
    const json: ApiResponse<{ data: PodcastItem[] }> = await response.json();
    return json.data?.data || [];
  },

  async get(id: string): Promise<PodcastResponse> {
    const response = await fetch(`${API_BASE_URL}/podcasts/${id}`, {
      headers: { Authorization: `Bearer ${getCookie('vault_access_token')}` },
    });
    if (!response.ok) throw new Error('Error fetching podcast');
    const json: ApiResponse<PodcastResponse> = await response.json();
    return json.data;
  },

  async create(input: PodcastCreateInput): Promise<PodcastItem> {
    const response = await fetch(`${API_BASE_URL}/podcasts`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(input),
    });
    if (!response.ok) throw new Error('Error creating podcast');
    const json: ApiResponse<PodcastItem> = await response.json();
    return json.data;
  },

  async update(
    id: string,
    input: Partial<PodcastCreateInput>,
    expectedUserId?: string,
  ): Promise<PodcastItem> {
    if (expectedUserId && auth.getUserId() !== expectedUserId)
      throw new ApiError(401, 'La cuenta cambió durante la sincronización');
    const response = await fetch(`${API_BASE_URL}/podcasts/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ...input, id }),
    });
    // ApiError carries the HTTP status so the offline queue can tell a
    // permanent rejection (4xx, drop the op) from a transient one (retry).
    if (!response.ok) throw new ApiError(response.status, 'Error updating podcast');
    const json: ApiResponse<PodcastItem> = await response.json();
    return json.data;
  },

  async delete(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/podcasts/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getCookie('vault_access_token')}` },
    });
    if (!response.ok) throw new Error('Error deleting podcast');
  },

  async getProgress(id: string): Promise<{
    progressSec: number;
    progressLang: string;
    progressFlex: number;
    progressAt: string;
  } | null> {
    const response = await fetch(`${API_BASE_URL}/podcasts/${id}/progress`, {
      headers: { Authorization: `Bearer ${getCookie('vault_access_token')}` },
    });
    if (!response.ok) return null;
    const json: ApiResponse<{
      progressSec: number;
      progressLang: string;
      progressFlex: number;
      progressAt: string;
    }> = await response.json();
    return json.data;
  },

  async saveProgress(
    id: string,
    data: { progressSec: number; progressLang: string; progressFlex: number },
  ): Promise<void> {
    await fetch(`${API_BASE_URL}/podcasts/${id}/progress`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    }).catch(() => {}); // fire and forget, non-blocking
  },

  async listShared(): Promise<PodcastItem[]> {
    const response = await fetch(`${API_BASE_URL}/podcasts/shared`, {
      headers: { Authorization: `Bearer ${getCookie('vault_access_token')}` },
    });
    if (!response.ok) throw new Error('Error fetching shared podcasts');
    const json: ApiResponse<PodcastItem[]> = await response.json();
    return json.data || [];
  },

  async toggleShare(id: string, shared: boolean): Promise<{ visibility: string }> {
    const response = await fetch(`${API_BASE_URL}/podcasts/${id}/share`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ shared }),
    });
    if (!response.ok) throw new Error('Error updating podcast visibility');
    const json: ApiResponse<{ visibility: string }> = await response.json();
    return json.data;
  },

  async getMetadata(url: string): Promise<{
    title: string;
    author: string;
    thumbnailUrl: string;
    description?: string;
    tags?: string[];
    duration?: string;
  }> {
    const response = await fetch(
      `${API_BASE_URL}/podcasts/metadata?url=${encodeURIComponent(url)}`,
      {
        headers: { Authorization: `Bearer ${getCookie('vault_access_token')}` },
      },
    );
    if (!response.ok) throw new Error('Error fetching metadata');
    const json: ApiResponse<{
      title: string;
      author: string;
      thumbnailUrl: string;
      description?: string;
      tags?: string[];
      duration?: string;
    }> = await response.json();
    return json.data;
  },
};
